import { RootState } from "@services/Redux";
import { Money } from "@utils/currency";
import { uniqueBy } from "@utils/functions";
import { useWindowSize } from "@utils/hooks";
import { Color, OrderStatus, Product, ProductVariation, UserRole, VariationsValuesPayload } from "@utils/types";
import * as React from "react";
import { useSelector } from "react-redux";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Styled from "./styled";

interface VariationsGridProps {
  product: Product;
  variations: ProductVariation[];
  onValuesChange: (payload: VariationsValuesPayload[]) => void;
  triggerChangeOnInputBlur?: boolean;
  onBlur?: () => void;
}

const VariationsGrid: React.FC<VariationsGridProps> = ({ product, variations, onValuesChange, triggerChangeOnInputBlur = false, onBlur }) => {
  const [open, setOpen] = React.useState(false);
  const [colorToShow, setColorToShow] = React.useState<string>("");
  const { data: companyData } = useSelector((state: RootState) => state.company);
  const user = useSelector((state: RootState) => state.app.user);
  const { width } = useWindowSize();
  const colors = uniqueBy(
    "id",
    variations.map((variation) => variation.color)
  );

  const sizes = uniqueBy(
    "id",
    variations.map((variation) => variation.size)
  );

  const order = useSelector((state: RootState) => state.order);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const [values, setValues] = React.useState<VariationsValuesPayload[]>(
    variations.map((variation) => ({
      variation: variation,
      quantity: order.data?.items?.find((item) => item.variation?.id === variation.id)?.quantity ?? 0,
    }))
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, variation: ProductVariation) => {
    const value = e.target.value;
    const newValues = values.map((item) => {
      if (item.variation.id === variation.id) {
        let quantity = parseInt(`${+value}`);
        if (product.stock_enabled && quantity > variation.stock && order?.data?.status === OrderStatus.CREATED) {
          quantity = variation.stock;
          alert(`Ajustamos a quantidade para a quantidade máxima disponível desta variação: ${variation.stock}`);
        }
        return { variation, quantity };
      }
      return item;
    });
    setValues(newValues);
  };

  const handleOnBlur = (e: React.FocusEvent<HTMLInputElement>, colorId: number, sizeId: number) => {
    if (triggerChangeOnInputBlur) {
      onBlur?.();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.currentTarget.blur();
    }
  };

  React.useEffect(() => {
    onValuesChange(values);
    // eslint-disable-next-line
  }, [values]);

  React.useEffect(() => {
    setValues(
      variations.map((variation) => ({
        variation: variation,
        quantity: order.data?.items?.find((item) => item.variation?.id === variation.id)?.quantity ?? 0,
      }))
    );
    // eslint-disable-next-line
  }, [order.data?.items, variations]);

  const getColorImage = (color: Color) => {
    return `${color.image.base_url}/colors/${color.id}/thumb_1/${color.image.key}`;
  };

  return (
    <>
      <Lightbox
        open={open}
        close={() => setOpen(false)}
        plugins={[Zoom]}
        carousel={{
          finite: true,
        }}
        zoom={{
          maxZoomPixelRatio: width < 768 ? 12 : 60,
        }}
        slides={[{ src: colorToShow }]}
      />
      <Styled.RootContainer>
        <Styled.GridTable>
          <thead>
            <tr>
              <td></td>
              {sizes.map((size) => (
                <td key={size.id}>
                  <div>{size.name}</div>
                </td>
              ))}
            </tr>
          </thead>
          <tbody>
            {colors.map((color) => (
              <tr key={color.id}>
                <td>
                  {color.image ? (
                    <Styled.ColorImage
                      onClick={() => {
                        setColorToShow(`${getColorImage(color)}`);
                        setOpen(true);
                      }}
                      src={`${getColorImage(color)}`}
                    />
                  ) : (
                    color.name
                  )}
                </td>
                {sizes.map((size) => {
                  const variation = variations.find((v) => v.color.id === color.id && v.size.id === size.id);
                  const available = variation !== undefined && (!product.stock_enabled || variation.stock > 0) && !order.loadingAddProductRequest;
                  const quantity = values.find((v) => v.variation.id === variation?.id)?.quantity ?? 0;
                  return (
                    <td key={size.id}>
                      <Styled.Input
                        id={`${color.id}-${size.id}-input`}
                        ref={inputRef}
                        onKeyDown={handleKeyDown}
                        type="number"
                        disabled={!available && ![UserRole.COMPANY_ADMIN, UserRole.COMPANY_SELLER].includes(user?.role ?? UserRole.CUSTOMER)}
                        placeholder={available ? (product.stock_enabled && companyData.show_stock_to_customer ? `${variation.stock}` : " ") : "."}
                        value={quantity > 0 ? quantity : ""}
                        onWheel={(e) => e.currentTarget.blur()}
                        onChange={(e) => {
                          if (variation) handleInputChange(e, variation);
                        }}
                        onBlur={(e) => {
                          handleOnBlur(e, color.id, size.id);
                        }}
                      />
                      <Styled.VariationPriceContainer>
                        {variation?.price !== product.lowest_price && variation?.price ? Money(variation?.price ?? 0).format() : ""}
                      </Styled.VariationPriceContainer>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </Styled.GridTable>
      </Styled.RootContainer>
    </>
  );
};

export default VariationsGrid;
